import { AfterViewInit, Directive, ElementRef, Input, NgZone, OnChanges, Renderer2, SimpleChanges } from '@angular/core';

@Directive({
    selector: '[appHighlightText]',
})
export class HighlightTextDirective implements AfterViewInit, OnChanges {
    @Input() appHighlightText: string;
    @Input() highlight: string;
    @Input() ignoreCase: boolean;

    private _isInit: boolean;

    private _wrapperElement: HTMLElement;
    private _elementBeforeHighlighted: HTMLElement;
    private _highlightedElement: HTMLElement;
    private _elementAfterHighlighted: HTMLElement;

    constructor(
        private _element: ElementRef,
        private _ngZone: NgZone,
        private _renderer2: Renderer2) {
    }

    ngAfterViewInit(): void {
        this._ngZone.runOutsideAngular(() => this.buildBaseElements());
    }

    buildBaseElements(): any {
        // Build html like
        // <span>
        //   <span>textBeforeHighlight</span>
        //   <span>highlightedText</span>
        //   <span>textAfterHighlight</span>
        // </span>

        this._wrapperElement = this._renderer2.createElement('span');
        this._renderer2.addClass(this._wrapperElement, 'highlight-text');

        this._elementBeforeHighlighted = this._renderer2.createElement('span');
        this._renderer2.addClass(this._elementBeforeHighlighted, 'highlight-text__before');
        this._renderer2.appendChild(this._wrapperElement, this._elementBeforeHighlighted);

        this._highlightedElement = this._renderer2.createElement('span');
        this._renderer2.addClass(this._highlightedElement, 'highlight-text__highlighted');
        this._renderer2.appendChild(this._wrapperElement, this._highlightedElement);

        this._elementAfterHighlighted = this._renderer2.createElement('span');
        this._renderer2.addClass(this._elementAfterHighlighted, 'highlight-text__after');
        this._renderer2.appendChild(this._wrapperElement, this._elementAfterHighlighted);

        this._renderer2.appendChild(this._element.nativeElement, this._wrapperElement);

        this._isInit = true;

        this.updateHighlight();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this._ngZone.runOutsideAngular(() => this.updateHighlight());
    }

    updateHighlight(): any {
        if (!this._isInit) {
            return;
        }

        this.clearText(this._elementBeforeHighlighted);
        this.clearText(this._highlightedElement);
        this.clearText(this._elementAfterHighlighted);

        if (this.appHighlightText == null) {
            return;
        }

        if (this.highlight == null) {
            // display without highlight
            this.addText(this._elementBeforeHighlighted, this.appHighlightText);
        }

        const indexOfText = this.findHighlightedText();
        if (indexOfText < 0) {
            // display without highlight
            this.addText(this._elementBeforeHighlighted, this.appHighlightText);
        }

        const textBeforeHighlight = this.appHighlightText.substring(0, indexOfText);
        const highlightedText = this.appHighlightText.substring(indexOfText, indexOfText + this.highlight.length);
        const textAfterHighlight = this.appHighlightText.substring(indexOfText + this.highlight.length);

        this.addText(this._elementBeforeHighlighted, textBeforeHighlight);
        this.addText(this._highlightedElement, highlightedText);
        this.addText(this._elementAfterHighlighted, textAfterHighlight);
    }

    private clearText(element: HTMLElement) {
        while (element.lastChild) {
            this._renderer2.removeChild(element, element.lastChild);
        }
    }

    private addText(element: HTMLElement, text: string) {
        const textElement = this._renderer2.createText(text);
        this._renderer2.appendChild(element, textElement);
    }

    private findHighlightedText() {
        if (this.ignoreCase) {
            return this.appHighlightText.toLocaleLowerCase().indexOf(this.highlight.toLocaleLowerCase());
        }

        return this.appHighlightText.indexOf(this.highlight);
    }
}
